import { useMemo } from 'react';
import {
  ArrowRight,
  Sparkles,
  Flame,
  Hourglass,
  LogOut,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
// W70 Phase 3 — Demo-Mode-Check fuer User-spezifische Sektionen
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useMarketRegime } from '@/hooks/use-market-regime';
import {
  DEFAULT_USER_SETTINGS,
  fetchPositions,
  fetchUserSettings,
} from '@/lib/queries';
import { computeAccountSummary } from '@/lib/paper-account';
// Markt-Evidenz-Tabelle (03.07.) ersetzt MarketRegimeBadge/MarketPhasePanel/
// MacroRegimePanel/SentimentRegimePanel/VixRiskAdjuster/CapitulationRadar
// in den Markt-Details (Karten bleiben im Repo).
import { MarktEvidenzTabelle } from '@/components/dashboard/MarktEvidenzTabelle';
import { AccountOverviewPanel } from '@/components/positions/AccountOverviewPanel';
import { OffeneKaufzonenCard } from '@/components/dashboard/OffeneKaufzonenCard';
import { FastReifCard } from '@/components/dashboard/FastReifCard';
import { AusDemRadarCard } from '@/components/dashboard/AusDemRadarCard';
import { ReversalRadarCard } from '@/components/dashboard/ReversalRadarCard';
// W67 Phase 5b — TradeJournalInsightsCard und PerformanceDashboardCard nach
// src/pages/Journal.tsx verschoben. Das StrategyBacktestPanel, das hier
// frueher mitgenannt war, ist am 11.08.2026 entfallen: Es zeigte die Bilanz
// der Phantom-Plaene, also einer Engine, nach der nicht mehr gehandelt wird.
import { WeeklyBriefCard } from '@/components/dashboard/WeeklyBriefCard';
// W59 — Position-Trading-Power-Ups
import { PositionHealthCard } from '@/components/dashboard/PositionHealthCard';
// Weltblick-Entrümpelung (03.07.): CatalystImpact, CorrelationHeatmap,
// ReEntryCandidates, TagPerformance, MistakeDetector, MorningBriefing +
// DailyMacroBias raus aus dem Dashboard (Komponenten bleiben im Repo).
import { offenesRisiko } from '@/lib/v6/risiko';
import { bindetEchtesGeld } from '@/lib/v6/spuren';
import type { Position } from '@/types/position';
// M2-Audit (2026-05-14): folgende Imports waren niedrig-Mehrwert-Eye-Candy
// und wurden aus dem Dashboard entfernt. Komponenten bleiben im Repo erhalten
// für eventuellen späteren Re-Use auf anderen Seiten:
//   - DataQualityPanel → gehört nach /admin/cron-health (Operator-Tool)
//   - CorrelationMatrixPanel → unbenutzbar bei großer Watchlist, später /lab
// Weltblick-Entrümpelung (03.07.): SectorHeatmapPanel raus — Sektor-Sicht
// liefert jetzt das Geldfluss-Radar (Welt statt nur Watchlist). Komponente
// bleibt im Repo (M2-Muster).
// M30 — Sentiment-Lesefunktion (Panel selbst ist in der Evidenz-Tabelle aufgegangen)
import { fetchLatestSentiment } from '@/components/dashboard/SentimentRegimePanel';
// Markt-Wetter + Geldfluss-Radar — Top-Down-Contrarian-Schicht (2026-07).
// Audit 08.08.2026: MarktWellenChart (gezeichnete Zyklus-Welle) → MarktlageCard
// (echte Messwerte mit Alter + echter SPY-Verlauf). Siehe MarktlageCard-Kopf.
import { MarktlageCard } from '@/components/dashboard/MarktlageCard';
// Audit 08.08.2026 — "Was ist zu tun" ganz nach oben. Der Nutzer handelt im
// Paper-Konto und bildet dieselben Trades bei Revolut nach; er braucht den
// Satz mit den konkreten Preisen, nicht eine Lage-Einschaetzung. Und er
// braucht ihn als ZUSTAND: Mail und Push sind fluechtig, der gestaffelte
// Ausstieg erzeugt aber eine zweite Handlung Wochen nach dem Kauf.
import { HandlungslisteCard } from '@/components/dashboard/HandlungslisteCard';
import { KapitalWarnungCard } from '@/components/dashboard/KapitalWarnungCard';
import { GeldflussRadarCard } from '@/components/dashboard/GeldflussRadarCard';
import { ZukunftsausblickCard } from '@/components/dashboard/ZukunftsausblickCard';
import { EntdeckenTeaser } from '@/components/dashboard/EntdeckenTeaser';
import { fetchLatestMacroBrief } from '@/lib/macro-brief-read';
import { useMoneyFlowRadar } from '@/hooks/use-money-flow-radar';
// Welle 33 — Markt-Catalysts naechste 14T (FOMC/CPI/PCE/NFP)
import { UpcomingCatalystsPanel } from '@/components/dashboard/UpcomingCatalystsPanel';
import { buildRealMarketPhaseInput } from '@/lib/v51/build-features';
import { computeMarketPhase } from '@/lib/v51/market-phase';
import { HeuteInbox } from '@/components/dashboard/HeuteInbox';
import { cn } from '@/lib/utils';

// M2-Audit (2026-05-14): LAYER_LABEL entfernt — wurde nur von der ebenfalls
// entfernten CompactWatchlistDistribution genutzt.
// Weltblick-Entrümpelung V2 (03.07.): BOTTOM_LINE_BG + BriefingRow entfernt
// (KI-Briefings-Archiv weg — Briefing lebt im Symbol-Detail).

export default function Dashboard() {
  const { isDemoMode } = useDemoMode();
  const regime = useMarketRegime();
  // W98 — Marktphase für Heute-Inbox + Wetter + Evidenz-Tabelle (EINE
  // Rechnung; seit 03.07. das volle Ergebnis inkl. Klartext-summary).
  const dashPhase = useMemo(() => {
    try {
      return computeMarketPhase(
        buildRealMarketPhaseInput({
          spyPrice: regime.spyPrice,
          spyFeatures: regime.spyFeatures,
          vix: regime.vix,
          sectorRotation: regime.sectorRotation,
        })
      );
    } catch {
      return null;
    }
  }, [regime.spyPrice, regime.spyFeatures, regime.vix, regime.sectorRotation]);
  const dashPhaseAggregate = dashPhase?.aggregate ?? null;
  // W67 Phase 4 — useEngineVersion entfernt (liefert seit Welle 7 immer 'v51').

  // Markt-Wetter — Top-Down-Contrarian-Verdict (F&G + VIX + Phase → ein Satz).
  const sentiment = useDataState({
    queryKey: ['weather-sentiment'],
    queryFn: () => fetchLatestSentiment(),
    ttlSeconds: 30 * 60,
    isEmpty: (s) => !s,
  });
  // Audit 08.08.2026 — computeMarketWeather hier entfernt. Es war der
  // alleinige Konsument und speiste die gezeichnete Zyklus-Welle. Das Modul
  // market-weather.ts bleibt bestehen: phantom-core nutzt den Gier-Wert
  // weiterhin als Bremse fuer prozyklische Kaeufe (ueber fetchMarketGreed,
  // das korrekt nach einem NICHT-leeren fg_index filtert).
  // `sentiment` wird weiter fuer die Evidenz-Tabelle unten gebraucht.
  // Geldfluss-Radar — nutzt die Regime-Quotes + 10 Extra-Töpfe.
  const flow = useMoneyFlowRadar(regime.rawQuotes);
  // Zukunftsausblick — der tägliche KI-Makro-Brief (Cron, 18:15).
  const macroBrief = useDataState({
    queryKey: ['macro-brief-latest'],
    queryFn: () => fetchLatestMacroBrief(),
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b,
  });

  // Watchlist- und Trade-Plan-Abfragen sind am 11.08.2026 entfallen. Die
  // Watchlist wurde nur gebraucht, um Plan-Einträge auf Symbole aufzulösen;
  // Trade-Pläne werden seit PR 8 nicht mehr erzeugt. Was zu kaufen ist, steht
  // in „Was ist zu tun" und in den offenen Kaufzonen.

  // Weltblick-Entrümpelung V2 (03.07.): briefings-Query entfernt — das
  // KI-Briefings-Archiv ist vom Dashboard verschwunden (lebt im Symbol-Detail).

  // Welle 8 / A5: strategies-Query entfernt — kein Footer-Link mehr.
  const openPositions = useDataState({
    queryKey: ['positions', 'open'],
    queryFn: (signal) => fetchPositions('open', signal),
    ttlSeconds: 60,
  });

  const closedPositions = useDataState({
    queryKey: ['positions', 'closed'],
    queryFn: (signal) => fetchPositions('closed', signal),
    ttlSeconds: 60,
  });

  const userSettings = useDataState({
    queryKey: ['user-settings'],
    queryFn: fetchUserSettings,
    ttlSeconds: 5 * 60,
  });

  // #41 (14.07.) — News-Spikes-Query + Deep-Dive vom Dashboard entfernt:
  // der News-Ton fließt als Konsens-Quelle ins Symbol-Detail.

  // PP4: Konto-Aggregator (gleiche Single Source of Truth wie Positionen-Seite)
  const accountSummary = useMemo(() => {
    const settings = userSettings.state.data ?? DEFAULT_USER_SETTINGS;
    return computeAccountSummary(
      settings,
      openPositions.state.data ?? [],
      closedPositions.state.data ?? []
    );
  }, [userSettings.state.data, openPositions.state.data, closedPositions.state.data]);

  // ===========================================================================
  // "Heute wichtig"-Aggregation — kombiniert Briefings, Pläne und Positionen
  // zu einer fokussierten Tagesvorbereitungs-Liste.
  // ===========================================================================

  // W98 — scoreMovers entfernt: Score-Delta ist keine Entscheidung;
  // die Heute-Inbox zeigt nur Handelbares.

  // `planTriggers` entfiel am 11.08.2026 mit der Inbox-Karte „Plan im
  // Trigger-Bereich": Sie verlinkte auf /plans, eine seit PR 7 gelöschte
  // Route, und zeigte Altbestand aus der abgeschalteten Plan-Engine.

  /** Offene Positions mit Stop-Risiko < 5 % über aktuellem Kurs. */
  const stopRiskPositions = useMemo(() => {
    const items = openPositions.state.data ?? [];
    return items
      .map((p) => {
        const distancePct =
          p.current_price && p.current_stop && p.current_price > 0
            ? (p.current_price - p.current_stop) / p.current_price
            : null;
        return { position: p, distancePct };
      })
      .filter(
        (x) =>
          x.distancePct !== null && x.distancePct >= 0 && x.distancePct <= 0.05
      )
      .sort(
        (a, b) =>
          (a.distancePct ?? Infinity) - (b.distancePct ?? Infinity)
      )
      .slice(0, 5);
  }, [openPositions.state.data]);


  // W98 — heuteWichtigEmpty entfernt: die Heute-Inbox hat ihren eigenen
  // „Heute nichts zu tun"-Zustand.

  // M2-Audit (2026-05-14): watchlistByLayer-useMemo entfernt — wurde nur von
  // CompactWatchlistDistribution genutzt. totalSymbols entfiel 03.07. mit der
  // Sektor-Heatmap (Weltblick-Entrümpelung).

  return (
    <div className="flex flex-col h-[100dvh] lg:h-screen overflow-y-auto">
      <header className="px-6 py-4 border-b border-zinc-800/60">
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-zinc-400 mt-0.5">
          Vier Fragen: Was ist jetzt zu tun, was reift heran, was läuft, was ist
          herausgefallen.
        </p>
      </header>

      {/* ===================================================================
          UMBAU 11.08.2026 — vier Bereiche statt einer Kartenwand.

          Vorher war die Startseite nach Themen sortiert (Heute, Markt,
          Konto, Gelegenheiten) und mischte darin Handlungen mit Beobachtung.
          Jetzt folgt sie der Reihenfolge, in der die Fragen tatsächlich
          gestellt werden: Muss ich HEUTE etwas tun? Reift etwas heran? Was
          läuft gerade? Was ist herausgefallen — und warum?

          Alles, was keine dieser vier Fragen beantwortet, steht darunter und
          ist eingeklappt. Es ist nicht gelöscht; es ist nur nicht die
          Erstinformation.
          =================================================================== */}
      <div className="flex-1 p-3 sm:p-6 space-y-6 max-w-7xl">
        {/* ============ A — JETZT HANDELN ============ */}
        <section>
          <SectionHeader
            icon={<Flame className="size-4 text-orange-400" />}
            title="Jetzt handeln"
            hint="Fällige Handlungen mit Preis. An den meisten Tagen ist dieser Bereich leer — das ist der Normalfall."
          />

          {/* Beantwortet die Frage des Bereichs wörtlich, deshalb zuerst.
              Wird bei jedem Aufruf aus den Kursen abgeleitet — eine fällige
              Handlung bleibt stehen, bis sie gebucht ist. */}
          <div className="mb-4">
            <HandlungslisteCard />
          </div>

          {/* Direkt darunter, weil es dieselbe Frage von der anderen Seite
              beantwortet: Die Handlungsliste sagt, was zu tun ist — diese
              Karte sagt, wenn das Konto es nicht mehr trägt. Sie rendert
              nichts, solange alles passt. */}
          <div className="mb-4 empty:mb-0">
            <KapitalWarnungCard
              summary={accountSummary}
              maxOffenesRisikoPct={
                (userSettings.state.data ?? DEFAULT_USER_SETTINGS).max_total_open_risk_pct
              }
            />
          </div>

          {/* Ereignisse, die keine Kaufhandlung sind, aber heute zählen:
              Position nahe am Stop, Krypto-Kauffenster, Markt-Ereignis. */}
          <div className="mb-4">
            <HeuteInbox
              phaseAggregate={dashPhaseAggregate}
              vixLive={regime.vix?.vix_close ?? null}
              cryptoSupportive={regime.crypto?.is_supportive ?? null}
              stopRisk={stopRiskPositions.map(({ position, distancePct }) => ({
                symbol: position.symbol,
                distancePct,
                stop: position.current_stop,
              }))}
            />
          </div>

          <OffeneKaufzonenCard />
        </section>

        {/* ============ B — FAST REIF ============ */}
        <section>
          <SectionHeader
            icon={<Hourglass className="size-4 text-amber-400" />}
            title="Fast reif"
            hint="Was heranreift — ohne Handlungsaufforderung. Der Bereich, der bisher fehlte."
          />
          <FastReifCard />
        </section>

        {/* ============ C — OFFENE POSITIONEN ============ */}
        {!isDemoMode && (
          <section className="space-y-3">
            <SectionHeader
              icon={<Wallet className="size-4 text-sky-400" />}
              title="Offene Positionen"
              hint="Was läuft, was es kostet, wenn alle Stops fallen."
            />
            <AccountOverviewPanel summary={accountSummary} compact />
            <OffenesRisikoZeile
              accountBalance={
                userSettings.state.data?.account_balance ?? DEFAULT_USER_SETTINGS.account_balance
              }
              maxTotalOpenRiskPct={
                userSettings.state.data?.max_total_open_risk_pct ??
                DEFAULT_USER_SETTINGS.max_total_open_risk_pct
              }
              positions={openPositions.state.data ?? []}
            />
            <PositionHealthCard />
          </section>
        )}

        {/* ============ D — AUS DEM RADAR ============ */}
        <section>
          <SectionHeader
            icon={<LogOut className="size-4 text-zinc-400" />}
            title="Aus dem Radar"
            hint="Was zuletzt beachtenswert war und es nicht mehr ist — mit Grund."
          />
          <AusDemRadarCard />
        </section>

        {/* ===================================================================
            Alles Weitere: Lage, Beobachtung, Rückschau. Es beantwortet keine
            der vier Fragen und steht deshalb unter ihnen.
            =================================================================== */}

        <div className="mb-3">
          <MarktlageCard />
        </div>

        {/* Zukunftsausblick — Szenarien mit Gedächtnis (zugeklappt 1 Zeile). */}
        <ZukunftsausblickCard brief={macroBrief.state.data ?? null} />

        {/* Entdecken-Teaser (13.07.) — macht den Welt-Scan sichtbar. */}
        <div className="mb-3">
          <EntdeckenTeaser />
        </div>

        {/* W98 — MARKT-DETAILS: Alle Markt-Panels hinter EINEM Aufklapper.
            Die Erstinformation liefert die Ampel-Leiste in der Heute-Inbox
            oben (Chip-Klick öffnet und scrollt hierher). */}
        <details id="markt-details" className="rounded-md border border-zinc-800/80 bg-zinc-900/30 group/markt">
          <summary className="cursor-pointer p-3 min-h-[44px] text-sm font-semibold text-zinc-200 hover:bg-zinc-800/40 list-none flex items-center gap-2">
            <span className="text-cockpit-accent inline-block transition-transform group-open/markt:rotate-90">▸</span>
            <span>Markt-Details</span>
            <span className="ml-auto text-[10px] text-zinc-500 font-normal">
              Beweise hinterm Wetter · Geldfluss-Radar
            </span>
          </summary>
          <div className="px-3 pb-3 space-y-3">
            {/* Markt-Evidenz-Tabelle (Agent-Inventur 03.07.): ersetzt 7 Karten
                durch 1 Zeile je Beweis-Strang. */}
            <MarktEvidenzTabelle
              sentiment={sentiment.state.data ?? null}
              vix={regime.vix?.vix_close ?? null}
              phase={dashPhase}
              stocksSupportive={regime.stocks?.is_supportive ?? null}
              cryptoSupportive={regime.crypto?.is_supportive ?? null}
            />
            {/* Geldfluss-Radar — der eigenständige zweite Strang (Welt-Töpfe). */}
            <GeldflussRadarCard radar={flow.radar} isLoading={flow.isLoading} />
          </div>
        </details>

        {/* Welle 33 — Markt-Catalysts naechste 14T (FOMC, CPI, PCE, NFP) */}
        <UpcomingCatalystsPanel daysAhead={14} />

        {/* Beobachtung ohne Kaufsignal. Bewusst NICHT unter „Fast reif":
            Der Reversal-Radar arbeitet mit einem Boden-Score, nicht mit der
            gemessenen Zustandsmaschine. Beides in einen Bereich zu legen
            hieße, zwei verschiedene Urteile als eines auszugeben. */}
        <details className="group/section rounded-md border border-zinc-800/80 bg-zinc-900/30">
          <summary className="cursor-pointer p-3 min-h-[44px] text-sm font-semibold text-zinc-200 hover:bg-zinc-800/40 list-none flex items-center gap-2">
            <span className="text-cockpit-accent inline-block transition-transform group-open/section:rotate-90">▸</span>
            <span>Beobachtung</span>
            <span className="ml-auto text-[10px] text-zinc-500 font-normal">
              bewertet nur · kein Kaufsignal
            </span>
          </summary>
          <div className="px-3 pb-3 space-y-3">
            <ReversalRadarCard />
            <WeeklyBriefCard />
          </div>
        </details>

        {/* W67 Phase 5b — Performance, Backtest + Reflexions-Tools im Journal. */}
        <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3 flex items-center gap-3 text-xs">
          <Sparkles className="size-4 text-blue-400 shrink-0" />
          <span className="text-zinc-400 flex-1">
            Performance, Backtest + Reflexions-Tools sind im Journal.
          </span>
          <a
            href="/journal#performance"
            className="inline-flex items-center gap-1 text-blue-300 hover:text-blue-200 focus-ring rounded px-1.5 py-0.5"
          >
            Tab "Performance &amp; Backtest"
            <ArrowRight className="size-3" />
          </a>
        </div>
      </div>
    </div>
  );
}

/**
 * Offenes Gesamtrisiko in einer Zeile.
 *
 * UMBAU 11.08.2026 — rechnet nicht mehr selbst, sondern liest `offenesRisiko`
 * aus lib/v6/risiko.ts. Das ist dieselbe Funktion, auf der die Kauf-Sperre
 * (PR 5) und der Datenbank-Auslöser beruhen. Vorher hatte diese Zeile ihre
 * eigene Rechnung — mit zwei folgenreichen Unterschieden:
 *
 *  1. Sie zählte das geplante Risiko aktiver TRADE-PLÄNE mit. Pläne werden
 *     seit PR 8 nicht mehr erzeugt. Die Tabellen-Inventur vom 11.08. zeigt
 *     `v5_trade_plans` mit NULL Zeilen — dieser Beitrag war praktisch also
 *     schon null. Er stand nur noch als Rechenweg im Code und hätte jede
 *     wiederbelebte Zeile stillschweigend als Risiko gewertet.
 *  2. Eine Position OHNE Stop trug null bei — sie fiel schlicht aus der
 *     Rechnung. `risikoBetrag` setzt dafür den vollen Einsatz an. Genau in
 *     diesem Fall zeigte die alte Zeile am deutlichsten zu wenig an.
 *
 * Ergebnis: Anzeige und Sperre nennen jetzt dieselbe Zahl. Vorher konnte die
 * Seite grün zeigen, während die Datenbank den Kauf zurückwies.
 */
function OffenesRisikoZeile({
  accountBalance,
  maxTotalOpenRiskPct,
  positions,
}: {
  accountBalance: number;
  maxTotalOpenRiskPct: number;
  positions: Position[];
}) {
  const offen = offenesRisiko(positions, accountBalance);
  const utilizationPct =
    maxTotalOpenRiskPct > 0 ? (offen.prozent / maxTotalOpenRiskPct) * 100 : 0;
  const status: 'safe' | 'moderate' | 'critical' | 'breach' =
    utilizationPct < 50 ? 'safe' : utilizationPct < 80 ? 'moderate' : utilizationPct < 100 ? 'critical' : 'breach';
  const barColor = {
    safe: 'bg-cockpit-success',
    moderate: 'bg-cockpit-accent',
    critical: 'bg-cockpit-warning',
    breach: 'bg-cockpit-danger',
  }[status];
  const textColor = {
    safe: 'text-cockpit-success',
    moderate: 'text-cockpit-accent',
    critical: 'text-cockpit-warning',
    breach: 'text-cockpit-danger',
  }[status];
  const coach = {
    safe: 'Spielraum für neue Trades.',
    moderate: 'Risiko okay — neue Trades nur bei sauberem Setup.',
    critical: 'Nahe am Limit — besser warten.',
    breach: 'Limit überschritten — keine neuen Trades.',
  }[status];
  // Ohne Stop gibt es kein definiertes Risiko; solche Positionen gehen mit
  // vollem Einsatz ein. Das muss dastehen, sonst wirkt die Zahl unerklärlich
  // hoch.
  const ohneStop = positions.filter(
    (p) => bindetEchtesGeld(p) && (p.current_stop === null || !(p.current_stop > 0))
  ).length;

  return (
    <div className="rounded-md border border-zinc-800/80 bg-zinc-900/30 p-3 flex items-center gap-3">
      <ShieldCheck className={cn('size-4 shrink-0', textColor)} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 text-xs">
          <span className="font-semibold text-zinc-200">Offenes Gesamtrisiko</span>
          <span className={cn('font-mono tabular-nums', textColor)}>
            {offen.prozent.toFixed(2)} % / Limit {maxTotalOpenRiskPct.toFixed(1)} %
          </span>
        </div>
        <div className="relative h-1.5 rounded-full bg-cockpit-surface2 mt-1.5 overflow-hidden">
          <div
            className={cn('absolute inset-y-0 left-0 rounded-full transition-all', barColor)}
            style={{ width: `${Math.min(100, utilizationPct)}%` }}
          />
        </div>
        <div className="text-[10px] text-zinc-500 mt-1">
          {coach}{' '}
          <span className="text-zinc-600">
            {offen.anzahl} {offen.anzahl === 1 ? 'echte Position' : 'echte Positionen'} · Papier
            und Experiment zählen nicht mit.
          </span>
          {ohneStop > 0 && (
            <span className="text-amber-400/80">
              {' '}
              {ohneStop} davon ohne Stop — mit vollem Einsatz gerechnet.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// M2-Audit (2026-05-14): CompactWatchlistDistribution-Funktion entfernt —
// gehört zur entfernten Dashboard-Sektion. Falls Layer-Counts später wieder
// gebraucht werden, in Sidebar-Footer oder Watchlist-Page neu aufbauen.


function SectionHeader({
  icon,
  title,
  hint,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  // W70 Phase 7 (2026-05-25) — number-Prop entfernt. Mini-Section-Counter
  // war inkonsistent (nur 1 Section nutze ihn) und visuell ueberlagert auf
  // schmalen Viewports. Konsistenz > sekundaere Hierarchie.
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-300 flex items-center gap-2">
          {icon}
          {title}
        </h2>
        {hint && <p className="text-xs text-zinc-500 mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
  );
}



// W98 — HeuteWichtigCard/HeuteWichtigItem entfernt (ersetzt durch HeuteInbox).
// Weltblick-Entrümpelung V2 (03.07.) — BriefingRow entfernt (Archiv-Grid weg).
